/**
 * Discount presets + stacked line-item discounts.
 *
 * A **preset** is a per-location reusable discount (name + type + amount)
 * managed in Settings → Offerings → Discounts. Applying a preset to a line item
 * snapshots its values into a **line-item discount**; any number of discounts
 * can stack on one line, each percentage computed against the line's
 * pre-discount base (never compounding).
 *
 * Mirrors the bookings-infra contract (core service):
 *   GET    /business-locations/{salonId}/discount-presets
 *   POST   /business-locations/{salonId}/discount-presets
 *   PUT    /business-locations/{salonId}/discount-presets/{presetId}
 *   DELETE /business-locations/{salonId}/discount-presets/{presetId}
 *   PUT    /business-locations/{businessLocationId}/discount-presets/quick-discounts
 * All money values are JSON numbers; error bodies are `{ message }`.
 */

export type DiscountType = "Fixed Amount" | "Percentage";

/** Ordered for pickers / segmented controls. */
export const DISCOUNT_TYPES: readonly DiscountType[] = [
  "Percentage",
  "Fixed Amount",
] as const;

/** Backend validation: a preset name is 1–100 chars after trimming. */
export const MAX_DISCOUNT_PRESET_NAME_LENGTH = 100;

/** Backend validation: a percentage discount can't exceed 100. */
export const MAX_DISCOUNT_PERCENTAGE = 100;

/** Backend validation: a location has at most three quick discounts. */
export const MAX_QUICK_DISCOUNTS = 3;

/** An active discount preset at a location. */
export interface DiscountPreset {
  /** uuid */
  id: string;
  name: string;
  discount_type: DiscountType;
  /** Dollars (Fixed Amount) or percent points (Percentage, e.g. 2.91). */
  amount: number;
  /**
   * Slot (1–3) among the location's quick discounts — one-click shortcuts in
   * the Transaction Modal / Quick Sale summary — or null when it isn't one.
   * Optional so responses from a backend without the field still type-check.
   */
  quick_position?: number | null;
}

/** GET /business-locations/{salonId}/discount-presets — active only, ordered by name. */
export interface DiscountPresetsListResponse {
  discount_presets: DiscountPreset[];
}

/** POST / PUT response envelope. */
export interface DiscountPresetResponse {
  discount_preset: DiscountPreset;
}

/** POST /business-locations/{salonId}/discount-presets body. */
export interface CreateDiscountPresetRequest {
  name: string;
  discount_type: DiscountType;
  amount: number;
}

/** PUT /business-locations/{salonId}/discount-presets/{presetId} body (any subset). */
export type UpdateDiscountPresetRequest = Partial<CreateDiscountPresetRequest>;

/**
 * PUT .../discount-presets/quick-discounts body: the location's quick
 * discounts in display order (at most MAX_QUICK_DISCOUNTS; `[]` clears).
 * Responds with every active preset, as the list GET does.
 */
export interface SetQuickDiscountsRequest {
  discount_preset_ids: string[];
}

/**
 * How a DELETE resolved: `soft` = the preset was used on at least one line item
 * and was archived (hidden from lists, history kept); `hard` = never used, so it
 * was permanently deleted.
 */
export type DiscountPresetDeleteMode = "hard" | "soft";

/** DELETE /business-locations/{salonId}/discount-presets/{presetId} response. */
export interface DeleteDiscountPresetResponse {
  success: boolean;
  id: string;
  mode: DiscountPresetDeleteMode;
}

/**
 * A discount applied to a transaction line item (a `line_item_discounts` row),
 * as returned on every booked service / product / service package line.
 */
export interface LineItemDiscount {
  /** uuid of the applied discount row. */
  id: string;
  /** uuid of the preset it came from; null for legacy / unlinked discounts. */
  discount_preset_id: string | null;
  /** Snapshot of the preset name at application time. */
  name: string;
  /** Snapshot of the type at application time. */
  discount_type: DiscountType;
  /** As applied: dollars (Fixed Amount) or percent points (Percentage). */
  amount: number;
  /** Dollars this discount takes off the line. */
  amount_dollars: number;
}

/**
 * A discount on a line as the apply-discount UI works with it: already applied
 * (carries the applied-discount `id`) or staged — a new application, or a
 * Quick Sale cart line before its transaction exists — with no `id` and the
 * preset it comes from. A `LineItemDiscount` satisfies this shape.
 */
export interface LineDiscountEntry {
  /** Applied-discount row id; absent while staged. */
  id?: string;
  /** Source preset; null for a legacy / unlinked discount. */
  discount_preset_id: string | null;
  name: string;
  discount_type: DiscountType;
  amount: number;
}

/** Keep (optionally re-value) a discount already applied to the line. */
export interface KeepLineItemDiscountInput {
  id: string;
  discount_type?: DiscountType;
  amount?: number;
}

/** Apply a preset to the line; type/amount default to the preset's values. */
export interface ApplyPresetLineItemDiscountInput {
  discount_preset_id: string;
  discount_type?: DiscountType;
  amount?: number;
}

/**
 * One entry of a line item's `discounts` array in a PUT /transactions/line-items
 * (or create-quick-sale) body. The array is the line's FULL discount set
 * (replace semantics) — `[]` removes every discount.
 */
export type LineItemDiscountInput =
  | KeepLineItemDiscountInput
  | ApplyPresetLineItemDiscountInput;
